import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCanvas, getBase64StringFromDataURL, hexValid } from 'utils';
import { ActionButtons, ButtonContainer, ActionButton, ColorContainer, SwatchContainer, Img } from './styles';
import { saveAs } from 'file-saver';
import {
  clearUpdateData,
  clearSerializedData,
  setExportModel,
  setExportedModelData,
  setBillboardImages,
} from 'store/actions/canvasActions';
import { clearMessages } from 'store/actions/appActions';
import { HorizontalSpacer, Body, DownloadOutlined } from 'components';
import { ShadowsEnabledOutlined } from 'components/icons/ShadowsEnabledOutlined';
import { ShadowsDisabledOutlined } from 'components/icons/ShadowsDisabledOutlined';
import { withTheme } from 'styled-components';
import JSZip from 'jszip';
import * as bins from 'assets/images/Bins.jpg';
import * as tray from 'assets/images/Tray.jpg';
import * as wood from 'assets/images/Wood.jpg';
import constants from 'constants';
import _ from 'lodash';

const { CANVAS_UPDATE_TYPES, CANVAS_UPDATE_METHODS } = constants;

const ActionArea = withTheme(props => {
  const { theme, guidURL } = props;

  const [ selectedTrayColor, setSelectedTrayColor ] = useState(null);
  const [ selectedWoodColor, setSelectedWoodColor ] = useState(null);
  const [ selectedBinColor, setSelectedBinColor ] = useState(null);
  const [ trayColorsOpen, setTrayColorsOpen ] = useState(false);
  const [ woodColorsOpen, setWoodColorsOpen ] = useState(false);
  const [ binColorsOpen, setBinColorsOpen ] = useState(false);

  const dispatch = useDispatch();
  const appStore = useSelector(state => state.app);
  const canvasStore = useSelector(state => state.canvas);
  const exportedModelData = useMemo(() => canvasStore.exportedModelData, [ canvasStore ]);
  const serializedData = useMemo(() => canvasStore.serializedData, [ canvasStore ]);
  const billboardImages = useMemo(() => canvasStore.billboardImages, [ canvasStore ]);
  const viewportShadows = serializedData?.metadata.viewportShadows;
  const materialVariants = serializedData?.metadata.materialVariants || [];
  const selectedTheme = appStore.theme;

  const trayVariants = materialVariants.filter(variant => _.includes(_.toLower(variant), 'fabric_'));
  const woodVariants = materialVariants.filter(variant => _.includes(_.toLower(variant), 'frame_'));
  const binVariants = materialVariants.filter(variant => _.includes(_.toLower(variant), 'tray_'));
  const white = theme.colors[selectedTheme].white;

  const trayColors = serializedData?.metadata.materials
    ?.filter(mat => trayVariants.includes(mat.materialId))
    .map(mat => ({ name: mat.name, hex: mat.albedoColor, texture: mat.albedoTexture?.url }));

  const woodColors = serializedData?.metadata.materials
  ?.filter(mat => woodVariants.includes(mat.materialId))
  .map(mat => ({ name: mat.name, hex: mat.albedoColor, texture: mat.albedoTexture?.url }));

  const binColors = serializedData?.metadata.materials
  ?.filter(mat => binVariants.includes(mat.materialId))
  .map(mat => ({ name: mat.name, hex: mat.albedoColor, texture: mat.albedoTexture?.url }));

  const updateArgs = type => ({
    dispatch,
    type,
    method: CANVAS_UPDATE_METHODS.UPDATE,
  });

  const cleanup = useCallback(() => {
    dispatch(clearUpdateData());
    dispatch(clearSerializedData());
    dispatch(clearMessages());
    dispatch(setExportedModelData(null));
    dispatch(setExportModel(false));
    dispatch(setBillboardImages([]));
  }, [ dispatch ]);

  useEffect(() => {
    const hasTrayColors = trayColors?.length > 0 && !selectedTrayColor;
    const hasWoodColors = woodColors?.length > 0 && !selectedWoodColor;
    const hasBinColors = binColors?.length > 0 && !selectedBinColor;

    if (hasTrayColors) setSelectedTrayColor(trayColors[0]?.texture || trayColors[0]?.hex);
    if (hasWoodColors) setSelectedWoodColor(woodColors[0]?.texture || woodColors[0]?.hex);
    if (hasBinColors) setSelectedBinColor(binColors[0]?.texture || binColors[0]?.hex);
  }, [ trayColors, woodColors, binColors, selectedBinColor, selectedTrayColor, selectedWoodColor ]);

  /* NOTE: This is the flow for exporting a model.
  * 1. ActionButton tells store to export model -- which is picked up by the Canvas component.
  * 2. Canvas component sets downloaded model data in store -- see `setDownloadedModel` prop on Canvas.
  * 3. The useEffect below is triggered by the new model data set in the store, and calls getZipFile.
  * 4. getZipFile creates a new JSZip object and adds the .glb model data to it and downloads the zip.
  * 5. Clean up on the store occurs.
  */
  const getZipFile = useCallback(async () => {
    if (!_.isEmpty(exportedModelData)) {
      const previewImage = billboardImages.find(image => image.name === 'perspective')?.imageData;
      const zip = new JSZip();

      zip.file('preview.png', getBase64StringFromDataURL(previewImage), { base64: true });

      for (const key in exportedModelData) {
        zip.file('model.glb', exportedModelData[key]);
        break;
      }

      const file = await zip.generateAsync({ type: 'blob' });
      saveAs(file, 'model.zip');
      cleanup();
    }
  }, [ exportedModelData, billboardImages, cleanup ]);

  useEffect(() => {
      getZipFile();
  }, [ exportedModelData, billboardImages, getZipFile ]);

  const buildVariantColors = (array, setSelected) => {
    const colors = array?.map((item, index) => {
      const { name: variantName, hex, texture } = item;

      return (
        <ColorContainer key={index}>
          <ActionButton
            theme={theme}
            $selectedTheme={selectedTheme}
            width={1.5}
            height={1.5}
            texture={texture || null}
            color={hex}
            onClick={() => {
              const type = CANVAS_UPDATE_TYPES.MESH;
              const inboundData = { variantName };
              updateCanvas(updateArgs(type), inboundData);
              setSelected(texture || item?.hex);
            }}
          />
        </ColorContainer>
      );
    });

    return colors;
  };

  return (
    <>
      {guidURL && (
        <>
          <ActionButton
            theme={theme}
            $selectedTheme={selectedTheme}
            position='absolute'
            bottom='4rem'
            right='3rem'
            onClick={() => {
              const type = CANVAS_UPDATE_TYPES.VIEWPORT;
              const inboundData = { shadows: !viewportShadows };

              updateCanvas(updateArgs(type), inboundData);
            }}
          >
            {viewportShadows
              ? <ShadowsDisabledOutlined width={24} color={white} />
              : <ShadowsEnabledOutlined width={24} color={white} />
            }
          </ActionButton>

          <Body theme={theme} $selectedTheme={selectedTheme} size='Small' color={white}>
            <ActionButton
              theme={theme}
              $selectedTheme={selectedTheme}
              position='absolute'
              bottom='4rem'
              right='7rem'
              onClick={() => dispatch(setExportModel(true))}
            >
              <DownloadOutlined theme={theme} $selectedTheme={selectedTheme} color={white} />
            </ActionButton>
          </Body>

          <ActionButtons>
            <ButtonContainer>
              <ColorContainer>
                <ActionButton
                  theme={theme}
                  $selectedTheme={selectedTheme}
                  color={hexValid(selectedTrayColor)}
                  texture={selectedTrayColor}
                  onClick={() => setTrayColorsOpen(!trayColorsOpen)}
                />
                  <SwatchContainer
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    onMouseLeave={() => setTrayColorsOpen(false)}
                    visible={trayColorsOpen}
                  >
                    {buildVariantColors(trayColors, setSelectedTrayColor)}
                  </SwatchContainer>

                  <Img src={tray?.default} alt='Tray' />
              </ColorContainer>

              <HorizontalSpacer size={24} />

              <ColorContainer>
                <ActionButton
                  theme={theme}
                  $selectedTheme={selectedTheme}
                  color={hexValid(selectedWoodColor)}
                  texture={selectedWoodColor}
                  onClick={() => setWoodColorsOpen(!woodColorsOpen)}
                  />
                    <SwatchContainer
                      theme={theme}
                      $selectedTheme={selectedTheme}
                      onMouseLeave={() => setWoodColorsOpen(false)}
                      visible={woodColorsOpen}
                    >
                      {buildVariantColors(woodColors, setSelectedWoodColor)}
                    </SwatchContainer>

                    <Img src={wood?.default} alt='Wood' />
              </ColorContainer>

              <HorizontalSpacer size={24} />

              <ColorContainer>
                <ActionButton
                  theme={theme}
                  $selectedTheme={selectedTheme}
                  color={hexValid(selectedBinColor)}
                  texture={selectedBinColor}
                  onClick={() => setBinColorsOpen(!binColorsOpen)}
                />
                  <SwatchContainer
                    theme={theme}
                    $selectedTheme={selectedTheme}
                    onMouseLeave={() => setBinColorsOpen(false)}
                    visible={binColorsOpen}
                  >
                    {buildVariantColors(binColors, setSelectedBinColor)}
                  </SwatchContainer>

                  <Img src={bins?.default} alt='Bins' />
              </ColorContainer>
            </ButtonContainer>
          </ActionButtons>
        </>
      )}
    </>
  );
});

export { ActionArea };